import { z } from 'zod'

import {
  resolveDataClass,
  unknownEntityError,
} from '../shared/entity-serializer'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import { PagePermaLink } from '@/const'

const NAMED_PAGES: Record<string, PagePermaLink> = {
  dashboard: PagePermaLink.DASHBOARD,
}

export interface NavigateToResult {
  navigated: true
  newContext: { dataClass: string; entityId?: string; entityTitle?: string }
}

export const navigateToTool = defineTool({
  name: 'navigate_to',
  description:
    'Navigate the app to a named page, an entity list page, or an entity detail page. Use this only when the user wants to physically move to a page — their intent is to browse the app, not to receive data in chat. If the user wants data, analysis, filtering, or grouping returned as a response, use list_entities or search_entities instead. For named top-level pages (dashboard) use the page parameter. For an entity list page, use dataClass alone. For an entity detail page, use {dataClass, id} where id MUST come from a tool result in the current turn (search_entities, list_entities, get_related, get_current_page). Never construct, guess, or copy an id from message history — if you do not have a fresh id from one of those tools, call search_entities first. Do NOT pass entity field values as URL params — this tool cannot filter entity lists. Navigation happens in the SAME window by default; pass newTab: true ONLY when the user explicitly asks to open a record in a new tab, and NEVER as a side effect of a create or update — writing a record does not mean the user wants to navigate to it.',
  inputSchema: () =>
    z.object({
      page: z
        .string()
        .optional()
        .describe(
          'Named top-level page to navigate to. Supported values: "dashboard". Use this instead of dataClass for pages that are not entity lists.',
        ),
      dataClass: z
        .string()
        .optional()
        .describe(
          'Entity type, e.g. Company, Opportunity, Ticket, Activity, Person, Lead, Order, Quote. Omit when using page.',
        ),
      id: z
        .string()
        .optional()
        .describe(
          'The _id of the record to navigate to, as returned by search_entities, list_entities, get_related, or get_current_page. Omit for list pages. Never construct or guess.',
        ),
      newTab: z
        .boolean()
        .optional()
        .describe(
          'Open the record in a new browser tab. Only valid with {dataClass, id}. Set to true ONLY when the user explicitly asks for a new tab; omit for same-window navigation.',
        ),
      timeTracking: z
        .object({
          tab: z
            .enum(['my-times', 'my-team', 'my-orders', 'all-times'])
            .optional()
            .describe('Which tab to activate'),
          teamStart: z
            .string()
            .optional()
            .describe('Team date range start (YYYY-MM-DD)'),
          teamEnd: z
            .string()
            .optional()
            .describe('Team date range end (YYYY-MM-DD)'),
          ordersStart: z
            .string()
            .optional()
            .describe('Orders date range start (YYYY-MM-DD)'),
          ordersEnd: z
            .string()
            .optional()
            .describe('Orders date range end (YYYY-MM-DD)'),
          leader: z.string().optional().describe('Leader user id, or "all"'),
        })
        .optional()
        .describe(
          'Only valid when navigating to the time-tracking page. Pass tab and/or date range params.',
        ),
    }),
  concurrencySafe: false,
  cacheable: false,

  async run(args) {
    const { page, dataClass, id, newTab, timeTracking } = args

    // Named top-level page (e.g. dashboard)
    if (page) {
      const permalink = NAMED_PAGES[page.toLowerCase()]
      if (!permalink) return { error: `Unknown page: ${page}` }
      const result: NavigateToResult = {
        navigated: true,
        newContext: { dataClass: page },
      }

      getHostRuntime().navigation.navigate({ permalink })
      return result
    }

    // Detail-page navigation: {dataClass, id} — validate id exists before navigating.
    if (id) {
      if (!dataClass) {
        return { error: 'dataClass is required when id is given' }
      }
      if (!resolveDataClass(dataClass)) {
        return unknownEntityError(dataClass)
      }

      const item = await getHostRuntime().data.get(dataClass, id)
      if (!item) {
        return {
          error: `${dataClass} with id ${id} not found`,
          suggestion:
            'Call search_entities to find the record by name, then use show_options to confirm.',
        }
      }

      const url = await getHostRuntime().navigation.urlFor(item)
      if (!url) {
        return { error: `Could not resolve URL for ${dataClass} ${id}` }
      }

      const result: NavigateToResult = {
        navigated: true,
        newContext: { dataClass, entityId: id },
      }

      getHostRuntime().navigation.navigate({ url, newTab })
      return result
    }

    // List-page navigation with optional time-tracking params
    if (!dataClass) {
      return { error: 'dataClass is required for list page navigation' }
    }
    if (!resolveDataClass(dataClass)) {
      return unknownEntityError(dataClass)
    }
    const permalink = getHostRuntime().data.getOverviewPageLink(dataClass)
    if (!permalink) return { error: `No overview page for: ${dataClass}` }

    const params: Record<string, string> = {}
    if (timeTracking) {
      for (const [k, v] of Object.entries(timeTracking)) {
        if (v != null) params[k] = String(v)
      }
    }

    const result: NavigateToResult = {
      navigated: true,
      newContext: { dataClass },
    }
    getHostRuntime().navigation.navigate({
      permalink,
      params: Object.keys(params).length > 0 ? params : undefined,
    })
    return result
  },
})
